import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { Alert } from '@mui/material';
import { useQuery, useSubscription } from '@apollo/client';
import { isEmpty } from 'lodash';
import AuthContext from '../../../auth/context/AuthContext';
import clientGraphql from '../../../../common/apollo-graphql';
import Referral from '../../../referral/models/Referral';
import GlobalLoader from '../../../common/components/loader/GlobalLoader';
import ReferralsTable from './ReferralsTable';

export default function CompletedReferrals({
  query,
  uuid,
  subscription,
  onViewLogs,
  onView,
  showAppointmentFilter = true,
  columns
}) {
  const currentUser = useContext(AuthContext);

  // ---------------- STATE ----------------
  const [parsedData, setParsedData] = useState({ rows: [], count: 0 });
  const [appointmentMaintained, setAppointmentMaintained] = useState('all');
  const [searchFilters, setSearchFilters] = useState({
    type: 'Name',
    patientName: '',
    birthdate: '',
  });
  const [closeError, setCloseError] = useState(false);

  const subscriptionsRef = useRef([]);
  const debounceRef = useRef(null);

  // ---------------- QUERY / SUBSCRIPTION ----------------
  // NOTE: Only pass stable initial variables, don't include appointmentMaintained state directly
  const { loading, data, error, refetch } = useQuery(query, {
    client: clientGraphql,
    variables: {
      uuid,
      patientName: null,
      birthdate: null,
      appointmentMaintained: 'all', // initial value
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: subscriptionData } = useSubscription(subscription, {
    variables: { clinic_id: currentUser?.clinics?.[0]?.id || null },
  });

  // ---------------- REFETCH HELPER ----------------
  const refetchData = useCallback(
    (filters) => {
      if (!refetch) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        refetch({
          uuid,
          patientName: filters.patientName || null,
          birthdate: filters.birthdate || null,
          appointmentMaintained: filters.appointmentMaintained || 'all',
        }).then(({ data }) => {
          if (!isEmpty(data) && !isEmpty(data.rows)) {
            setParsedData({
              rows: data.rows.map((item) => ({
                ...item,
                referral: new Referral(item),
                actions: {
                  onViewNotes: onViewLogs,
                  onView: onView,
                },
              })),
              count: data.rows.length,
            });
          } else {
            setParsedData({ rows: [], count: 0 });
          }
        });
      }, 400);
    },
    [refetch, uuid, onView, onViewLogs]
  );

  // ---------------- FILTER HANDLERS ----------------
  const handleFilterChange = (e) => {
    const value = e.target.value;
    const newFilters = { ...searchFilters };
    if (searchFilters.type === 'Name') newFilters.patientName = value;
    else newFilters.birthdate = value;

    setSearchFilters(newFilters);
    refetchData({ ...newFilters, appointmentMaintained });
  };

  const handleTypeChange = (e) => {
    const newFilters = {
      type: e.target.value,
      patientName: '',
      birthdate: '',
      appointmentMaintained,
    };
    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  const handleAppointmentMaintainedChange = (e) => {
    const newValue = e.target.value;
    setAppointmentMaintained(newValue);
    refetchData({ ...searchFilters, appointmentMaintained: newValue });
  };

  // ---------------- SUBSCRIPTION EFFECT ----------------
  useEffect(() => {
    if (!isEmpty(subscriptionData)) {
      if (
        !isEmpty(subscriptionsRef.current) &&
        JSON.stringify(subscriptionData) !== JSON.stringify(subscriptionsRef.current)
      ) {
        refetchData({ ...searchFilters, appointmentMaintained });
      }
      subscriptionsRef.current = subscriptionData;
    }
  }, [subscriptionData, refetchData, searchFilters, appointmentMaintained]);

  // ---------------- DATA EFFECT ----------------
  useEffect(() => {
    if (!isEmpty(data) && !isEmpty(data.rows)) {
      setParsedData({
        rows: data.rows.map((item) => ({
          ...item,
          referral: new Referral(item),
          actions: {
            onViewNotes: onViewLogs,
            onView: onView,
          },
        })),
        count: data.rows.length,
      });
    }
  }, [data, onViewLogs, onView]);

  if (loading) return <GlobalLoader />;

  return (
    <>
      {error && !closeError && (
        <Alert variant="filled" severity="error" onClose={() => setCloseError(true)}>
          We are sorry, but we are having difficulty with our system. We are unable to load data
          at the moment. Please reach out to us if you continue to experience this issue.
        </Alert>
      )}

      <ReferralsTable
        onFilterChanged={handleFilterChange}
        onChangeDropdown={handleTypeChange}
        onAppointmentMaintainedFilterChanged={showAppointmentFilter ? handleAppointmentMaintainedChange : undefined}
        loading={loading}
        refresh={() => refetchData({ ...searchFilters, appointmentMaintained })}
        data={!isEmpty(parsedData) ? parsedData : { rows: [], count: 0 }}
        columns={columns}
      />
    </>
  );
}
